import { Link } from 'react-router';
import { useAuth } from '@/contexts/auth-context';
import { useClaimsStore } from '@/stores/claims-store';
import { useEffect } from 'react';

export function DashboardPage() {
  const { user } = useAuth();
  const { claims, isLoading, error, fetchClaims } = useClaimsStore();

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  const recentClaims = claims.slice(0, 3);
  const draftClaims = claims.filter((c) => c.status === 'draft');
  const activeClaims = claims.filter((c) => !['draft', 'resolved'].includes(c.status));
  const isNewUser = claims.length === 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome header */}
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-brand-950">
          Welcome{isNewUser ? '' : ' back'}, {user?.name?.split(' ')[0] || 'there'}! 👋
        </h1>
        <p className="mt-2 text-surface-800">
          {isNewUser 
            ? "Let's get you started with your pothole damage claim. Mac will guide you every step of the way."
            : "Here's an overview of your pothole damage claims"
          }
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-surface-600">Loading your dashboard...</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-error-50 border border-error-200 rounded-xl p-8 text-center" role="alert">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="font-display text-xl font-semibold text-error-800 mb-2">
            We couldn't load your claims
          </h2>
          <p className="text-sm text-error-700 mb-6 max-w-md mx-auto">{error}</p>
          <button
            onClick={() => fetchClaims()}
            className="inline-flex items-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Try again
          </button>
        </div>
      ) : isNewUser ? (
        /* New User Experience */
        <>
          {/* Getting Started Hero */}
          <div className="bg-gradient-to-br from-brand-600 via-brand-700 to-brand-800 rounded-2xl p-8 mb-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative">
              <span className="inline-block px-3 py-1 bg-white/20 text-white text-sm font-medium rounded-full mb-4">
                🚀 Getting Started
              </span>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-3">
                Ready to make your claim?
              </h2>
              <p className="text-brand-100 max-w-2xl mb-6">
                Mac will help you gather evidence, find the responsible council, and prepare a professional 
                claim pack. Most claims take just 10-15 minutes to complete.
              </p>
              <Link
                to="/claims/new"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-brand-700 font-bold rounded-xl hover:bg-brand-50 transition-colors text-lg shadow-lg hover:shadow-xl"
              >
                Start My Claim
                <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>

          {/* How It Works - Simple Steps */}
          <div className="mb-8">
            <h2 className="font-display text-xl font-bold text-brand-950 mb-6">
              How it works – 4 simple steps
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  step: '1',
                  title: 'Tell us what happened',
                  description: 'When and where did the pothole damage your vehicle?',
                  icon: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  ),
                },
                {
                  step: '2',
                  title: 'Upload your evidence',
                  description: 'Photos, invoices, receipts – Mac analyses them for you',
                  icon: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  ),
                },
                {
                  step: '3',
                  title: 'Mac prepares your claim',
                  description: 'Professional documents ready for the council',
                  icon: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  ),
                },
                {
                  step: '4',
                  title: 'Submit & track',
                  description: 'Download your pack and send to the council',
                  icon: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ),
                },
              ].map((item) => (
                <div 
                  key={item.step}
                  className="bg-white rounded-xl border border-surface-200 p-5 hover:border-brand-300 hover:shadow-md transition-all"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-8 h-8 bg-brand-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {item.step}
                    </span>
                    <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center text-brand-600">
                      {item.icon}
                    </div>
                  </div>
                  <h3 className="font-semibold text-brand-950 mb-1">{item.title}</h3>
                  <p className="text-sm text-surface-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* What You'll Need */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl border border-surface-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-success-500/10 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <h3 className="font-display text-lg font-semibold text-brand-950">
                  What you'll need
                </h3>
              </div>
              <ul className="space-y-3">
                {[
                  { text: 'Photos of the pothole', required: true },
                  { text: 'Photos of damage to your vehicle', required: true },
                  { text: 'Repair invoice or quote', required: true },
                  { text: 'Date and location of incident', required: true },
                  { text: 'Your vehicle details (reg, make, model)', required: true },
                  { text: 'Dashcam footage', required: false },
                  { text: 'Witness details', required: false },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    {item.required ? (
                      <svg className="w-5 h-5 text-success-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-surface-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    )}
                    <span className={item.required ? 'text-surface-900' : 'text-surface-600'}>
                      {item.text}
                      {!item.required && <span className="text-surface-400 ml-1">(optional)</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl border border-surface-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-display text-lg font-semibold text-brand-950">
                  Frequently asked
                </h3>
              </div>
              <div className="space-y-4">
                {[
                  {
                    q: 'How long does a claim take?',
                    a: 'Creating your claim pack takes 10-15 mins. Councils typically respond within 8-12 weeks.',
                  },
                  {
                    q: 'Do I need a repair invoice?',
                    a: 'Yes, you\'ll need either an invoice for completed repairs or a quote for pending work.',
                  },
                  {
                    q: 'What are my chances of success?',
                    a: 'If the council knew about the pothole, your chances are good. Mac checks their records for you.',
                  },
                  {
                    q: 'How much does it cost?',
                    a: 'Just £14.99 for a complete claim pack. No hidden fees.',
                  },
                ].map((item, i) => (
                  <div key={i}>
                    <p className="font-medium text-brand-950 text-sm">{item.q}</p>
                    <p className="text-sm text-surface-600 mt-0.5">{item.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Reassurance */}
          <div className="bg-surface-50 rounded-xl p-6 text-center">
            <div className="flex items-center justify-center gap-6 flex-wrap">
              <div className="flex items-center gap-2 text-surface-700">
                <svg className="w-5 h-5 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-sm font-medium">Secure & Private</span>
              </div>
              <div className="flex items-center gap-2 text-surface-700">
                <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span className="text-sm font-medium">AI-Powered Analysis</span>
              </div>
              <div className="flex items-center gap-2 text-surface-700">
                <svg className="w-5 h-5 text-warning-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium">Takes 10-15 minutes</span>
              </div>
            </div>
          </div>
        </>
      ) : (
        /* Returning User Experience */
        <>
          {/* Stats */}
          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl border border-surface-200 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-surface-800">Total Claims</p>
                  <p className="font-display text-2xl font-bold text-brand-950">{claims.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-surface-200 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-warning-500/10 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-warning-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-surface-800">Drafts</p>
                  <p className="font-display text-2xl font-bold text-brand-950">{draftClaims.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-surface-200 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-success-500/10 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-surface-800">Active</p>
                  <p className="font-display text-2xl font-bold text-brand-950">{activeClaims.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="bg-gradient-to-br from-brand-600 to-brand-700 rounded-2xl p-8 mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="font-display text-2xl font-bold text-white mb-2">
                  Start a new claim
                </h2>
                <p className="text-brand-100 max-w-md">
                  Had another pothole damage your vehicle? Start gathering evidence and Mac will help
                  you prepare a professional claim.
                </p>
              </div>
              <Link
                to="/claims/new"
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-brand-700 font-semibold rounded-xl hover:bg-brand-50 transition-colors whitespace-nowrap"
              >
                New Claim
                <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </Link>
            </div>
          </div>

          {/* Draft Claims Alert */}
          {draftClaims.length > 0 && (
            <div className="bg-warning-500/10 border border-warning-500/20 rounded-xl p-4 mb-8">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-warning-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="font-medium text-warning-800">
                    You have {draftClaims.length} unfinished {draftClaims.length === 1 ? 'claim' : 'claims'}
                  </p>
                  <p className="text-sm text-warning-700 mt-0.5">
                    Complete your draft{draftClaims.length === 1 ? '' : 's'} to submit to the council.
                  </p>
                </div>
                <Link
                  to={`/claims/${draftClaims[0]?.id}`}
                  className="ml-auto text-sm font-medium text-warning-700 hover:text-warning-800"
                >
                  Continue →
                </Link>
              </div>
            </div>
          )}

          {/* Recent claims */}
          <div className="bg-white rounded-xl border border-surface-200">
            <div className="px-6 py-4 border-b border-surface-200 flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-brand-950">
                Recent Claims
              </h2>
              <Link to="/claims" className="text-sm text-brand-600 hover:text-brand-700 font-medium">
                View all
              </Link>
            </div>

            <ul className="divide-y divide-surface-200">
              {recentClaims.map((claim) => (
                <li key={claim.id}>
                  <Link
                    to={`/claims/${claim.id}`}
                    className="flex items-center justify-between px-6 py-4 hover:bg-surface-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-brand-950">
                          {claim.locationAddress || 'Location not set'}
                        </p>
                        <p className="text-sm text-surface-800">
                          {new Date(claim.incidentDate).toLocaleDateString('en-GB')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          claim.status === 'draft'
                            ? 'bg-surface-100 text-surface-800'
                            : claim.status === 'pack_generated'
                            ? 'bg-success-500/10 text-success-600'
                            : 'bg-brand-100 text-brand-700'
                        }`}
                      >
                        {claim.status.replace('_', ' ')}
                      </span>
                      <svg className="w-5 h-5 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
